import React from 'react';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Link from '@mui/material/Link';
import Typography from '@mui/material/Typography';
import { ArrowLeft, ExternalLink, MessageSquarePlus, X } from 'lucide-react';

import PropertyLabels from 'src/components/labels/PropertyLabels';
import {
    formatPropertyAddress,
    getPropertyDetailRows,
} from 'src/components/property-details-card/property-detail-rows';
import type { IProperty } from 'src/services/api/properties/types';

import 'src/styles/map-surface.css';
import styles from 'src/components/property-details-card/PropertyDetailsCard.module.css';

type Props = {
    property: IProperty;
    /** Extra fact shown at the top right, e.g. distance from the lead's own property. */
    badge?: string;
    onEdit?: () => void;
    /** Renders a back control, for when the card replaced a list the user came from. */
    onBack?: () => void;
    backLabel?: string;
    /** Renders a dismiss control in the header, for when the card is the whole panel. */
    onClose?: () => void;
    /**
     * Renders an "Add to message" action that drops a pitch for this property into the
     * lead-conversation composer. Only meaningful where a composer exists.
     */
    onAddToMessage?: () => void;
};

/**
 * Everything known about one property, for the panel that opens when a map marker is
 * selected. Shared by the properties-page map and the similar-properties map so both
 * surface the same facts.
 */
export default function PropertyDetailsCard({
    property,
    badge,
    onEdit,
    onBack,
    backLabel = 'All units',
    onClose,
    onAddToMessage,
}: Props) {
    const rows = getPropertyDetailRows(property);

    return (
        <div className={styles.card} data-testid={`property-details-card-${property.id}`}>
            <div className={styles.header}>
                {property.image_url && (
                    <img
                        src={property.image_url}
                        alt=""
                        className={styles.thumbnail}
                        loading="lazy"
                    />
                )}
                <div className={styles.heading}>
                    <Typography variant="subtitle2" fontWeight={600} className={styles.name}>
                        {property.name}
                    </Typography>
                    <Typography variant="caption" className={styles.address}>
                        {formatPropertyAddress(property)}
                    </Typography>
                </div>
                {badge && (
                    <Typography variant="caption" className={styles.badge}>
                        {badge}
                    </Typography>
                )}
                {onClose && (
                    <IconButton
                        size="small"
                        aria-label="Close property details"
                        className={styles.close}
                        onClick={onClose}
                        data-testid={`property-details-close-${property.id}`}
                    >
                        <X size={16} />
                    </IconButton>
                )}
            </div>

            <div className={styles.chips}>
                <span
                    className={property.is_vacant ?
                        `${styles.statusChip} ${styles.statusChipVacant}` :
                        styles.statusChip}
                >
                    <span className={styles.statusDot} />
                    {property.is_vacant ? 'Vacant' : 'Occupied'}
                </span>
                {property.is_income_capped && (
                    <span className={`${styles.statusChip} ${styles.statusChipCapped}`}>
                        Income capped
                    </span>
                )}
            </div>

            <div className={styles.body}>
                {rows.length > 0 ? (
                    <div className={styles.rows}>
                        {rows.map((row) => (
                            <React.Fragment key={row.key}>
                                <Typography variant="caption" className={styles.rowLabel}>
                                    {row.label}
                                </Typography>
                                <Typography
                                    variant="body2"
                                    className={row.isHighlighted ?
                                        `${styles.rowValue} ${styles.rowValueHighlighted}` :
                                        styles.rowValue}
                                >
                                    {row.value}
                                </Typography>
                            </React.Fragment>
                        ))}
                    </div>
                ) : (
                    <Typography variant="body2" className={styles.empty}>
                        No details recorded for this property yet.
                    </Typography>
                )}

                {property.labels.length > 0 && (
                    <div className={styles.section}>
                        <Typography variant="caption" className={styles.sectionLabel}>
                            Labels
                        </Typography>
                        <PropertyLabels labels={property.labels} maxVisible={4} multiline />
                    </div>
                )}

                {(property.showing_scheduling_link || property.application_link) && (
                    <div className={styles.section}>
                        <Typography variant="caption" className={styles.sectionLabel}>
                            Links
                        </Typography>
                        <div className={styles.links}>
                            {property.showing_scheduling_link && (
                                <Link
                                    href={property.showing_scheduling_link.link}
                                    target="_blank"
                                    rel="noreferrer"
                                    variant="body2"
                                    className={styles.link}
                                    data-testid={`property-details-tour-link-${property.id}`}
                                >
                                    Schedule a tour
                                    <ExternalLink size={13} />
                                </Link>
                            )}
                            {property.application_link && (
                                <Link
                                    href={property.application_link.link}
                                    target="_blank"
                                    rel="noreferrer"
                                    variant="body2"
                                    className={styles.link}
                                    data-testid={`property-details-application-link-${property.id}`}
                                >
                                    Apply
                                    <ExternalLink size={13} />
                                </Link>
                            )}
                        </div>
                    </div>
                )}

                {property.notes && (
                    <div className={styles.section}>
                        <Typography variant="caption" className={styles.sectionLabel}>
                            Notes
                        </Typography>
                        <Typography variant="body2" className={styles.notes}>
                            {property.notes}
                        </Typography>
                    </div>
                )}
            </div>

            {(onBack || onEdit || onAddToMessage) && (
                <div className={styles.actions}>
                    {onBack && (
                        <Button
                            size="small"
                            startIcon={<ArrowLeft size={14} />}
                            onClick={onBack}
                            data-testid={`property-details-back-${property.id}`}
                        >
                            {backLabel}
                        </Button>
                    )}
                    <span className={styles.actionsSpacer} />
                    {onAddToMessage && (
                        <Button
                            size="small"
                            variant="contained"
                            disableElevation
                            startIcon={<MessageSquarePlus size={14} />}
                            onClick={onAddToMessage}
                            data-testid={`property-details-add-to-message-${property.id}`}
                        >
                            Add to message
                        </Button>
                    )}
                    {onEdit && (
                        <Button
                            size="small"
                            variant="outlined"
                            onClick={onEdit}
                            data-testid={`property-details-edit-${property.id}`}
                        >
                            Edit
                        </Button>
                    )}
                </div>
            )}
        </div>
    );
}
